import { useEffect, useState } from "react";
import { apiClient, readError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, CheckCircle2, XCircle, Clock, Banknote, AlertCircle } from "lucide-react";

const STATUS_META = {
  pending: { label: "En attente du demandeur", color: "bg-amber-50 text-amber-800 border-amber-400", icon: Clock },
  accepted: { label: "Accepté · paiement initié", color: "bg-blue-50 text-blue-800 border-blue-400", icon: Banknote },
  paid: { label: "Payé · séquestré", color: "bg-emerald-50 text-emerald-800 border-emerald-400", icon: CheckCircle2 },
  rejected: { label: "Refusé", color: "bg-red-50 text-red-700 border-red-300", icon: XCircle },
  cancelled: { label: "Annulé", color: "bg-zinc-50 text-zinc-700 border-zinc-300", icon: XCircle },
};

const fmtEUR = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function MissionAvenants({ missionId, mission, user, journalUpdates }) {
  const [data, setData] = useState({ avenants: [], base_amount: 0, avenants_paid_total: 0, grand_total: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ amount: "", description: "" });
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const isOwner = user?.id === mission?.demandeur_id;
  const isSelectedArtisan = user?.id === mission?.selected_artisan_id;
  const canPropose = isSelectedArtisan && mission?.status === "paid";
  const visible = mission?.status === "paid" || mission?.status === "completed";

  const load = async () => {
    try {
      const { data: d } = await apiClient.get(`/missions/${missionId}/avenants`);
      setData(d);
    } catch (e) {
      console.warn(readError(e));
    }
  };

  useEffect(() => {
    if (visible) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, visible]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 100) {
      toast.error("Description trop courte (100 caractères minimum)");
      return;
    }
    if (selectedPhotos.length === 0) {
      toast.error("Sélectionnez au moins 1 photo justificative");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/avenants", {
        mission_id: missionId,
        amount: parseFloat(form.amount),
        description: form.description.trim(),
        justification_update_ids: selectedPhotos,
      });
      toast.success("Avenant proposé · en attente du demandeur");
      setOpen(false);
      setForm({ amount: "", description: "" });
      setSelectedPhotos([]);
      await load();
    } catch (err) {
      toast.error(readError(err));
    } finally {
      setBusy(false);
    }
  };

  const accept = async (av) => {
    if (!window.confirm(`Accepter l'avenant n°${av.number} pour ${fmtEUR(av.amount)} ? Vous serez redirigé vers Stripe pour le paiement séquestre.`)) return;
    setBusy(true);
    try {
      const { data: res } = await apiClient.post("/avenants/checkout", {
        avenant_id: av.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(readError(e));
      setBusy(false);
    }
  };

  const reject = async (av) => {
    if (!window.confirm(`Refuser l'avenant n°${av.number} ? L'artisan en sera notifié.`)) return;
    setBusy(true);
    try {
      await apiClient.post(`/avenants/${av.id}/reject`);
      toast.success("Avenant refusé");
      await load();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (av) => {
    if (!window.confirm("Annuler votre avenant ?")) return;
    setBusy(true);
    try {
      await apiClient.post(`/avenants/${av.id}/cancel`);
      toast.success("Avenant annulé");
      await load();
    } catch (e) {
      toast.error(readError(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePhoto = (id) => {
    setSelectedPhotos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const myJournalPhotos = (journalUpdates || []).filter(
    (u) => u.uploaded_by === user?.id && u.file_type === "image"
  );

  if (!visible) return null;

  return (
    <div className="border-2 border-[#0A0A0B] bg-white" data-testid="mission-avenants">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="display text-2xl flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#FF4D00]" /> Avenants (Travaux Supplémentaires)
        </div>
        {canPropose && (
          <button
            onClick={() => setOpen((o) => !o)}
            className="btn-anthracite flex items-center gap-2 text-xs"
            data-testid="avenant-toggle-form"
          >
            <Plus className="w-4 h-4" /> Proposer un avenant
          </button>
        )}
      </div>

      {/* Recap totals */}
      <div className="grid grid-cols-3 border-b-2 border-zinc-200" data-testid="avenants-totals">
        <div className="p-4 border-r-2 border-zinc-200">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Devis initial</div>
          <div className="display text-2xl mt-1">{fmtEUR(data.base_amount)}</div>
        </div>
        <div className="p-4 border-r-2 border-zinc-200">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Avenants payés</div>
          <div className="display text-2xl mt-1 text-[#FF4D00]">+{fmtEUR(data.avenants_paid_total)}</div>
        </div>
        <div className="p-4 bg-[#0A0A0B] text-white">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Total cumulé</div>
          <div className="display text-2xl mt-1">{fmtEUR(data.grand_total)}</div>
        </div>
      </div>

      {/* Propose form */}
      {open && canPropose && (
        <form onSubmit={submit} className="border-b-2 border-zinc-200 p-6 bg-zinc-50 space-y-4" data-testid="avenant-form">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Nouvel avenant</div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">Montant (€ TTC)</label>
            <input
              type="number" step="0.01" min="1" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white"
              data-testid="avenant-amount"
              placeholder="Ex: 1250"
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">
              Description détaillée (100 caractères min · {form.description.length}/2000)
            </label>
            <textarea
              rows="5" required minLength={100} maxLength={2000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 bg-white"
              data-testid="avenant-description"
              placeholder="Décrivez précisément les travaux supplémentaires, leur justification technique, les matériaux additionnels et le délai estimé."
            />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-zinc-600">
              Preuves photo (≥1, depuis votre journal de chantier)
            </label>
            {myJournalPhotos.length === 0 ? (
              <div className="mt-2 border-2 border-dashed border-amber-400 bg-amber-50 p-3 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>Aucune photo trouvée dans votre journal. Publiez d'abord une photo (étape "En cours" ou "Étape clé") dans la section Journal ci-dessus pour pouvoir la sélectionner ici.</span>
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {myJournalPhotos.map((u) => {
                  const active = selectedPhotos.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => togglePhoto(u.id)}
                      className={`relative border-2 p-2 text-left transition ${active ? "border-[#FF4D00] bg-orange-50" : "border-zinc-300 bg-white hover:border-[#0A0A0B]"}`}
                      data-testid={`avenant-photo-${u.id}`}
                    >
                      <div className="mono text-[9px] uppercase tracking-widest font-semibold">{u.stage}</div>
                      <div className="text-[10px] text-zinc-600 mt-1 line-clamp-2">{u.caption || u.original_name || "(sans légende)"}</div>
                      {active && <CheckCircle2 className="absolute top-1 right-1 w-4 h-4 text-[#FF4D00]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy || selectedPhotos.length === 0 || form.description.length < 100}
              className="btn-batisur flex-1"
              data-testid="avenant-submit"
            >
              {busy ? "Envoi…" : "Soumettre l'avenant"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setForm({ amount: "", description: "" }); setSelectedPhotos([]); }}
              className="px-4 py-2 border-2 border-zinc-300 mono text-[11px] uppercase tracking-widest hover:bg-zinc-100"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {data.avenants.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm" data-testid="avenants-empty">
          Aucun avenant proposé pour cette mission.
        </div>
      ) : (
        <ul className="divide-y-2 divide-zinc-100">
          {data.avenants.map((av) => {
            const meta = STATUS_META[av.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <li key={av.id} className="p-5" data-testid={`avenant-item-${av.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="display text-xl">Avenant n°{av.number}</span>
                      <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 font-semibold flex items-center gap-1 ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <span className="display text-xl text-[#FF4D00]">+{fmtEUR(av.amount)}</span>
                    </div>
                    <p className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{av.description}</p>
                    <div className="mt-2 mono text-[10px] uppercase tracking-widest text-zinc-500">
                      Proposé le {new Date(av.created_at).toLocaleDateString("fr-FR")} · {av.justification_update_ids?.length || 0} preuve(s) photo
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {isOwner && av.status === "pending" && (
                      <>
                        <button
                          onClick={() => accept(av)}
                          disabled={busy}
                          className="btn-batisur text-xs flex items-center gap-2"
                          data-testid={`avenant-accept-${av.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4" /> Accepter & payer
                        </button>
                        <button
                          onClick={() => reject(av)}
                          disabled={busy}
                          className="px-4 py-2 border-2 border-red-300 text-red-700 mono text-[11px] uppercase tracking-widest hover:bg-red-50"
                          data-testid={`avenant-reject-${av.id}`}
                        >
                          Refuser
                        </button>
                      </>
                    )}
                    {isOwner && av.status === "accepted" && (
                      <button
                        onClick={() => accept(av)}
                        disabled={busy}
                        className="btn-batisur text-xs flex items-center gap-2"
                        data-testid={`avenant-pay-${av.id}`}
                      >
                        <Banknote className="w-4 h-4" /> Reprendre le paiement
                      </button>
                    )}
                    {isSelectedArtisan && av.status === "pending" && (
                      <button
                        onClick={() => cancel(av)}
                        disabled={busy}
                        className="px-4 py-2 border-2 border-zinc-400 text-zinc-700 mono text-[11px] uppercase tracking-widest hover:bg-zinc-100"
                        data-testid={`avenant-cancel-${av.id}`}
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
